import { APP_INITIALIZER, ApplicationConfig, ErrorHandler, LOCALE_ID, importProvidersFrom } from '@angular/core';
import * as AWS from 'aws-sdk/global';
import { Amplify } from 'aws-amplify';
import * as Sentry from '@sentry/angular-ivy';
import en from '@angular/common/locales/en';
import es from '@angular/common/locales/es';
import fr from '@angular/common/locales/fr';
import { NZ_I18N, en_US, es_ES, fr_FR } from 'ng-zorro-antd/i18n';
import { environment } from 'src/environments/environment';
import { createSentryCustomErrorHandler } from './sentry-custom-error-handler';
import { Router, provideRouter, withComponentInputBinding } from '@angular/router';
import { IconsProviderModule } from './icons-provider.module';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { registerLocaleData } from '@angular/common';
import { APP_ROUTES } from './app.routes';
import { NzMessageModule } from 'ng-zorro-antd/message';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzModalModule } from 'ng-zorro-antd/modal';

AWS.config.update({
  region: environment.AWS_REGION,
  maxRetries: 1,
  httpOptions: {
    timeout: 0,
    connectTimeout: 2000,
  },
});

Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: environment.IDENTITY_POOL_ID,
      userPoolId: environment.USER_POOL_ID,
      userPoolClientId: environment.USER_POOL_WEB_CLIENT_ID,
    },
  },
  API: {
    REST: {
      api: {
        region: environment.AWS_REGION,
        endpoint: environment.API_URL,
      },
    },
  },
});

registerLocaleData(en);
registerLocaleData(es);
registerLocaleData(fr);

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: NZ_I18N,
      useFactory: (localId: string): unknown => {
        switch (localId) {
          case 'en':
            return en_US;
          /** keep the same with angular.json/i18n/locales configuration **/
          case 'es':
            return es_ES;
          case 'fr':
            return fr_FR;
          default:
            return en_US;
        }
      },
      deps: [LOCALE_ID],
    },
    {
      provide: ErrorHandler,
      useValue: createSentryCustomErrorHandler({
        showDialog: false,
        logErrors: true,
      }),
    }, // we only use Sentry in production see also main.ts
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => (): void => {
        //
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideRouter(APP_ROUTES, withComponentInputBinding()),
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      IconsProviderModule,
      NzBadgeModule,
      NzMessageModule,
      NzModalModule,
    ),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
};

Sentry.init({
  dsn: environment.SENTRY_DSN,
  transportOptions: {
    maxQueueSize: 100,
  },
  environment: environment.sentry_env,
  ignoreErrors: ['Non-Error exception captured'],
  tracesSampleRate: 0,
  enabled: true,
});
